import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { User, Mail, GraduationCap, Users, BookOpen, Coins, Save, ArrowLeft, Loader2, Globe, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";
import { languages } from "../i18n";

const ProfilePage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, token, setUser, setToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    role: user?.role || "student"
  });

  const getRoleIcon = (role) => {
    switch (role) {
      case "teacher":
        return <GraduationCap className="w-5 h-5" />;
      case "parent":
        return <Users className="w-5 h-5" />;
      default:
        return <BookOpen className="w-5 h-5" />;
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.put(`${API}/auth/profile`, formData, {
        headers: getAuthHeaders(token)
      });
      setUser(response.data);
      toast.success(t('success.saved'));
      setEditMode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
    toast.success(t('success.saved'));
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/auth/account`, {
        headers: getAuthHeaders(token)
      });
      
      // Clear auth state and local storage
      setUser(null);
      setToken(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      toast.success(t('profile.accountDeleted'));
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="profile-page">
      <DashboardHeader />

      <main className="container-main py-8">
        <Button 
          variant="ghost" 
          className="mb-6 gap-2"
          onClick={() => navigate("/dashboard")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')} {t('nav.dashboard')}
        </Button>

        <div className="max-w-2xl mx-auto space-y-6">
          {/* Profile Header */}
          <Card className="card-base">
            <CardContent className="p-4 sm:p-8">
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <Avatar className="w-20 h-20 sm:w-24 sm:h-24">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-600 text-2xl sm:text-3xl">
                    {user?.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-center sm:text-left">
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {user?.name}
                  </h1>
                  <p className="text-slate-500 mb-2 text-sm sm:text-base break-all">{user?.email}</p>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-4">
                    <span className="flex items-center gap-1 px-2 sm:px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs sm:text-sm font-medium">
                      {getRoleIcon(user?.role)}
                      <span className="capitalize">{user?.role}</span>
                    </span>
                    <span className="flex items-center gap-1 px-2 sm:px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs sm:text-sm font-medium">
                      <Coins className="w-3 h-3 sm:w-4 sm:h-4" />
                      {user?.credits} credits
                    </span>
                  </div>
                </div>
                {!editMode && (
                  <Button 
                    variant="outline" 
                    onClick={() => setEditMode(true)}
                    data-testid="edit-profile-btn"
                    className="mt-2 sm:mt-0"
                  >
                    {t('profile.editProfile')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Edit Form or Account Info */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                {editMode ? t('profile.editProfile') : t('profile.accountInfo')}
              </CardTitle>
              <CardDescription>
                {editMode ? t('profile.editProfile') : t('profile.accountInfo')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {editMode ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('auth.name')}</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-12"
                      data-testid="name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">{t('auth.role')}</Label>
                    <Select
                      value={formData.role}
                      onValueChange={(value) => setFormData({ ...formData, role: value })}
                    >
                      <SelectTrigger className="h-12" data-testid="role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="student">
                          <div className="flex items-center gap-2">
                            <BookOpen className="w-4 h-4" />
                            {t('auth.roleStudent')}
                          </div>
                        </SelectItem>
                        <SelectItem value="teacher">
                          <div className="flex items-center gap-2">
                            <GraduationCap className="w-4 h-4" />
                            {t('auth.roleTeacher')}
                          </div>
                        </SelectItem>
                        <SelectItem value="parent">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            {t('auth.roleParent')}
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditMode(false);
                        setFormData({ name: user?.name || "", role: user?.role || "student" });
                      }}
                      disabled={loading}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={loading}
                      className="btn-primary"
                      data-testid="save-profile-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          {t('profile.saveChanges')}
                        </>
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('auth.name')}</Label>
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700">{user?.name}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('auth.email')}</Label>
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700">{user?.email}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('auth.role')}</Label>
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      {getRoleIcon(user?.role)}
                      <span className="text-slate-700 capitalize">{user?.role}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('profile.memberSince')}</Label>
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-700">
                        {user?.created_at 
                          ? new Date(user.created_at).toLocaleDateString() 
                          : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Language Settings */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }} className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                {t('profile.language')}
              </CardTitle>
              <CardDescription>{t('profile.languageDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={i18n.language} onValueChange={handleLanguageChange}>
                <SelectTrigger className="w-full md:w-64 h-12" data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      <div className="flex items-center gap-2">
                        <img src={lang.flag} alt={lang.code} className="w-5 h-4 rounded-sm object-cover" />
                        <span>{lang.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Credits Info */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{t('common.credits')}</CardTitle>
              <CardDescription>{t('pricing.currentCredits')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-teal-50 rounded-xl mb-4">
                <div>
                  <p className="text-sm text-slate-500">{t('pricing.currentCredits')}</p>
                  <p className="text-3xl font-bold text-slate-900">{user?.credits} {t('common.credits')}</p>
                </div>
                <Button 
                  onClick={() => navigate("/pricing")}
                  className="btn-accent"
                  data-testid="buy-credits-btn"
                >
                  <Coins className="w-4 h-4 mr-2" />
                  {t('pricing.buyCredits')}
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-slate-500">{t('pricing.bookAnalysis')}</p>
                  <p className="font-medium">15 {t('common.credits')}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-slate-500">Test (10 {t('tests.questions')})</p>
                  <p className="font-medium">5 {t('common.credits')}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-slate-500">Quiz (10 {t('tests.questions')})</p>
                  <p className="font-medium">3 {t('common.credits')}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-slate-500">{t('common.download')}</p>
                  <p className="font-medium">2 {t('common.credits')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delete Account */}
          <Card className="card-base border-red-200">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }} className="text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                {t('profile.dangerZone')}
              </CardTitle>
              <CardDescription>{t('profile.deleteAccountDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="destructive" 
                    className="gap-2"
                    data-testid="delete-account-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('profile.deleteAccount')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                      <AlertTriangle className="w-5 h-5" />
                      {t('profile.deleteAccountConfirmTitle')}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-left space-y-2">
                      <p>{t('profile.deleteAccountConfirmDesc')}</p>
                      <ul className="list-disc list-inside text-sm space-y-1 mt-2">
                        <li>{t('profile.deleteDataBooks')}</li>
                        <li>{t('profile.deleteDataTests')}</li>
                        <li>{t('profile.deleteDataQuizzes')}</li>
                        <li>{t('profile.deleteDataNotes')}</li>
                        <li>{t('profile.deleteDataCredits')}</li>
                      </ul>
                      <p className="font-semibold text-red-600 mt-3">{t('profile.deleteAccountWarning')}</p>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteAccount}
                      disabled={deleteLoading}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="confirm-delete-btn"
                    >
                      {deleteLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Trash2 className="w-4 h-4 mr-2" />
                      )}
                      {t('profile.deleteAccountConfirm')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
